import { useContext, useState } from "react";
import { Query, QueryResult } from "@material-table/core";
import { useIntl } from "react-intl";
import { ProductsSchema } from "@feature/types";
import useAsyncError from "@hooks/useAsyncError";
import {
  buildFilterFromQuery,
  MTColumnsType,
  orderingFromQuery,
} from "@utils/pagination";
import { NotificationsContext } from "@ui-components/Notifications";
import _ from "lodash";
import useCountryAxiosInstance from "hooks/useCountryAxiosInstance";

interface PaginatedResultSchema {
  items: ProductsSchema[];
  total: number;
}

export const useProducts = () => {
  const intl = useIntl();

  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const apiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();

  // state
  const [loading, setLoading] = useState(false);

  // utilities
  const upsertErrorHandler = (e: any) => {
    let message: string | undefined;

    if (e.response?.status !== 400) message = "malformed_data_error";
    else if (e.response?.data.detail === "product_already_registered")
      message = intl.formatMessage({ id: "product_already_registered" });
    else if (e.response?.data.detail === "product_required_fields")
      message = intl.formatMessage({ id: "product_required_fields" });
    // if a message is set, show it and return false in order to avoid rethrowing the error...
    if (message) {
      push({ type: "error", title: message });
      return false;
    }
    // ...otherwise return true in order to rethrow it
    return true;
  };
  const baseHandler = async <T>(
    callback: () => Promise<T>,
    onSuccess?: () => void,
    onError?: (e: any) => boolean
  ): Promise<T | null> => {
    setLoading(true);
    try {
      const result = await callback();
      onSuccess?.();
      return result;
    } catch (e: any) {
      const rethrow = !!onError ? onError(e) : true;

      if (rethrow) throwError(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // handlers
  const fetch = async (
    query: Query<ProductsSchema>,
    columns: MTColumnsType<ProductsSchema>
  ) => {
    const callback = async () => {
      const payload = {
        filters: (query && buildFilterFromQuery(query, columns)) || null,
        ordering: (query && orderingFromQuery(query)) || null,
        offset: query!.page * query!.pageSize,
        limit: query!.pageSize,
      };
      const { items, total }: PaginatedResultSchema = await apiRequest!.post(
        "/products/all",
        payload
      );
      return {
        data: items,
        totalCount: total,
        page: query!.page,
      } as QueryResult<ProductsSchema>;
    };
    return await baseHandler<QueryResult<ProductsSchema>>(callback);
  };
  const add = async (data: ProductsSchema) => {
    const callback = async () => {
      return (await apiRequest!.post("/products", data)) as ProductsSchema;
    };
    return await baseHandler<ProductsSchema | null>(
      callback,
      () => {
        push({
          type: "success",
          title: intl.formatMessage({ id: "product_created_successfully" }),
        });
      },
      upsertErrorHandler
    );
  };
  const update = async (newData: ProductsSchema, oldData?: ProductsSchema) => {
    const callback = async () => {
      if (!oldData || _.isEqual(newData, oldData)) return null;
      return (await apiRequest!.post(`/products/${oldData.id}`, newData)) as
        | number
        | null;
    };
    return await baseHandler<number | null>(
      callback,
      () => {
        push({
          type: "success",
          title: intl.formatMessage({ id: "product_updated_successfully" }),
        });
      },
      upsertErrorHandler
    );
  };

  return { fetch, add, update, loading };
};
